import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  FILTER_CONTENT_ITEM,
  ISelectInput,
  SearchSelector,
  SearchOperator,
} from '@portal/shared/ui/filter';
import { FilterStaticBase } from '@portal/shared/ui/filter/src/lib/components/static-base';
import { TransactionFilterList } from '../transaction-filters.list';
import { FallbackModeList } from './fallback-mode.list';
import { FiltersService } from '@portal/shared/ui/filter/src/lib/services/filters.service';
import { TransactionFilter } from '../../../enums/transaction-filter.enum';
import cloneDeep from 'lodash/cloneDeep';
import { Subscription } from 'rxjs';
import { Directions } from '@portal/shared/core/src/lib/enums/direction.enum';
import { FilterSubMenuService } from '../../../services/filter-sub-menu.service';
import { SupportedProcessorSettingsService } from '../../../../../services/supported-processor-settings.service';

@Component({
  selector: 'portal-transaction-filter-fallback-mode',
  templateUrl: './fallback-mode.component.html',
  providers: [
    {
      provide: FILTER_CONTENT_ITEM,
      useExisting: FilterTransactionFallbackModeComponent,
    },
  ],
  standalone: false,
})
export class FilterTransactionFallbackModeComponent extends FilterStaticBase {
  @Input() isMainDropdownFilter: boolean;
  @Input() showFilterSubMenu: boolean;
  @Input() isHeightAuto = false;
  @Input() displayFromFilter = false;
  @Output()
  fallbackModeTextName: EventEmitter<string> = new EventEmitter();
  fallbackModeText = this.transactionfilters.list.find(
    (filter) => filter.id === TransactionFilter.FallbackMode,
  ).text;
  readonly Directions = Directions;

  constructor(
    private fallbackModes: FallbackModeList,
    private transactionfilters: TransactionFilterList,
    private filtersService: FiltersService,
    private filterSubMenuService: FilterSubMenuService,
    private supportedProcessorSettingsService: SupportedProcessorSettingsService,
  ) {
    super(SearchSelector.FallbackMode, fallbackModes.list);
    this.intermediatePreSelectedEntities = new Map();
    this.addButtonLabel = $localize`Add`;

    this.filtersService.actionRemoveFilter$.subscribe((entityToRemove: ISelectInput) => {
      if (SearchSelector.FallbackMode === entityToRemove.key) {
        const selectedEntity = this.localPreSelectedEntities.get(entityToRemove.id);
        if (selectedEntity?.id) {
          this.onEntityRemoved(entityToRemove);
          this.onFilterApplied();
          this.itemToggled.emit();
        }
      }
    });

    this.subMenuComponentSelected$.subscribe((submenu) => {
      if (SearchSelector.FallbackMode.includes(submenu)) {
        this.synchronizeLocalPreSelectedEntities();
      }
      this.updateEntities();
    });

    this.updateEntities$.subscribe(() => {
      if (this.isMainDropdownFilter) {
        this.synchronizeLocalPreSelectedEntities();
      }
      this.updateEntities();
    });

    this.updateCounter$.subscribe(() => {
      const localPreSelected: Map<string, ISelectInput> = new Map();
      // Check if FallbackMode filter is applied with (ELECTRONIC_FALLBACK) argument
      if (this.criteriaMapPreSelected.has(SearchSelector.FallbackMode)) {
        const argumentStr = this.criteriaMapPreSelected.get(SearchSelector.FallbackMode)?.argument;
        if (argumentStr && argumentStr.includes('ELECTRONIC_FALLBACK')) {
          const electronicFallback = {
            id: 'ELECTRONIC_FALLBACK',
            text: 'Electronic Fallback'
          };
          localPreSelected.set('ELECTRONIC_FALLBACK', electronicFallback);
          this.localPreSelectedEntities = cloneDeep(localPreSelected);
          this.intermediatePreSelectedEntities = cloneDeep(localPreSelected);
        }
      } else {
        // Make sure to clear both when there's no pre-selected filter
        this.localPreSelectedEntities.clear();
        this.intermediatePreSelectedEntities.clear();
      }
      this.updateEntities();
    });
  }

  addIntermediateEntity(selectedEntity: ISelectInput): void {
    this.intermediatePreSelectedEntities.set(selectedEntity.id, selectedEntity);
    this.setAddButtonLabel();
  }

  onEntityRemoved(selectedEntity: ISelectInput): void {
    this.intermediatePreSelectedEntities.delete(selectedEntity.id);
    this.setAddButtonLabel();
  }

  onFilterAddedAndEntitiesUpdated(): void {
    this.entities = this.fallbackModes.list;
    this.localPreSelectedEntities.clear();
    this.intermediatePreSelectedEntities.forEach((entity) => {
      this.localPreSelectedEntities.set(entity.id, entity);
    });
    this.onFilterAdded();
    this.updateEntities();
    if (this.isOpen) {
      this.closeSubmenus$.next(true);
    }
    this.fallbackModeTextName.emit(this.fallbackModeText);
    this.isOpen = false;
  }

  onFilterAdded(): void {
    this.updateEntities();
    const values: string[] = this.getValues();
    if (values.length) {
      // Keep it simple - same pattern as other filters
      // The backend needs to handle the mapping from fallbackMode to electronicFallback
      this.filterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        operator: SearchOperator.In,
        value: Array.from(this.localPreSelectedEntities.values()),
        argument: `(${values.join(',')})`,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        value: Array.from(this.localPreSelectedEntities.values()),
      });
    } else {
      this.filterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        toRemove: true,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        toRemove: true,
      });
    }

    this.searchedText = '';
    this.itemToggled.emit();
    this.updateSwitchToggleState();
  }

  protected override subscribeToRemoveEntityObservable(): Subscription {
    return this.entityRemoved$.subscribe((entityToRemove: ISelectInput) => {
      const selectedEntity = this.localPreSelectedEntities.get(entityToRemove.id);
      if (selectedEntity) {
        this.onEntityRemoved(entityToRemove);
        this.onFilterAdded();
        this.isOpen = false;
      }
    });
  }

  private synchronizeLocalPreSelectedEntities(): void {
    // Check if FallbackMode filter is active with (ELECTRONIC_FALLBACK)
    if (this.criteriaMapPreSelected?.has(SearchSelector.FallbackMode)) {
      const argumentStr = this.criteriaMapPreSelected.get(SearchSelector.FallbackMode)?.argument;
      if (argumentStr && argumentStr.includes('ELECTRONIC_FALLBACK')) {
        const electronicFallback = {
          id: 'ELECTRONIC_FALLBACK',
          text: 'Electronic Fallback'
        };
        this.localPreSelectedEntities.clear();
        this.intermediatePreSelectedEntities.clear();
        this.localPreSelectedEntities.set('ELECTRONIC_FALLBACK', electronicFallback);
        this.intermediatePreSelectedEntities.set('ELECTRONIC_FALLBACK', electronicFallback);
      }
    } else {
      this.localPreSelectedEntities.clear();
      this.intermediatePreSelectedEntities.clear();
    }
    this.updateEntities();
    this.setAddButtonLabel();
  }

  onClear(): void {
    this.clearStaticMultiSelect();
    this.entities = this.fallbackModes.list;
    this.removeStaticFilter();
    this.fallbackModeTextName.emit(this.fallbackModeText);
  }
}